import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function FloatingCTA() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight * 0.6;
      setVisible(window.scrollY > heroHeight);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
          className="fixed bottom-0 left-0 right-0 z-50 px-3 md:px-6 pb-3 md:pb-5 pointer-events-none"
        >
          <div className="max-w-3xl mx-auto pointer-events-auto">
            <div className="bg-white/95 backdrop-blur-xl border border-gray-200/80 rounded-2xl shadow-2xl shadow-navy-900/15 px-4 md:px-6 py-3 md:py-4 flex items-center gap-3 md:gap-5 relative overflow-hidden">
              {/* Subtle gradient accent */}
              <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-primary-500 via-amber-500 to-primary-500"></div>

              {/* Left icon */}
              <div className="hidden sm:flex w-10 h-10 items-center justify-center bg-primary-50 rounded-xl border border-primary-100 flex-shrink-0">
                <i className="ri-focus-3-line text-primary-600 text-lg"></i>
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-bold text-navy-900 leading-tight">
                  Build or Scale?
                </p>
                <p className="text-xs md:text-sm text-gray-500 hidden sm:block">
                  Choose your path — both start with a free consultation.
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                <Link
                  to="/software-development"
                  onClick={() => {
                    if (typeof window !== 'undefined' && (window as Record<string, unknown>).gtag) {
                      (window as Record<string, unknown>).gtag('event', 'cta_click', {
                        event_category: 'floating_cta',
                        event_label: 'start_project',
                      });
                    }
                  }}
                  className="px-3 md:px-4 py-2 bg-primary-500 text-white text-xs md:text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-primary-500/20 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <span className="hidden md:inline">Start a Project</span>
                  <span className="md:hidden">Build</span>
                </Link>
                <Link
                  to="/talent-solutions"
                  onClick={() => {
                    if (typeof window !== 'undefined' && (window as Record<string, unknown>).gtag) {
                      (window as Record<string, unknown>).gtag('event', 'cta_click', {
                        event_category: 'floating_cta',
                        event_label: 'hire_talent',
                      });
                    }
                  }}
                  className="px-3 md:px-4 py-2 bg-navy-900 text-white text-xs md:text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-navy-900/20 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <span className="hidden md:inline">Hire Talent</span>
                  <span className="md:hidden">Scale</span>
                </Link>

                {/* Dismiss */}
                <button
                  onClick={() => setDismissed(true)}
                  className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0 ml-1"
                  aria-label="Dismiss"
                >
                  <i className="ri-close-line text-gray-400 text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}